import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { supabase, isSupabaseReady } from '../lib/supabase';
import { getJoinRef } from '../lib/onboardingRef';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user,         setUser]        = useState(null);
  const [token,        setToken]        = useState(localStorage.getItem('spaczo_token'));
  const [favorites,    setFavorites]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('spaczo_favorites') || '[]'); } catch { return []; }
  });
  const [recoveryMode, setRecoveryMode] = useState(false);
  const loggingOutRef = useRef(false);

  // Restore session on mount
  useEffect(() => {
    // Handle OAuth redirect callbacks (access_token in URL hash)
    if (window.location.hash.includes('access_token=')) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const tokenType   = hashParams.get('type');
      if (accessToken) {
        window.history.replaceState({}, document.title, window.location.pathname);
        if (tokenType === 'recovery') {
          // Manually set Supabase session (detectSessionInUrl is disabled)
          if (isSupabaseReady()) {
            const refreshToken = hashParams.get('refresh_token') || '';
            supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
              .then(() => setRecoveryMode(true))
              .catch(() => setRecoveryMode(true));
          } else {
            setRecoveryMode(true);
          }
        } else {
          // Google OAuth flow
          axios.post('/api/auth/google', { accessToken, ref: getJoinRef() || undefined })
            .then(res => {
              if (res.data.success) {
                const { token: t, user: u } = res.data;
                setToken(t); setUser(u);
                localStorage.setItem('spaczo_token', t);
                localStorage.setItem('spaczo_user', JSON.stringify(u));
                axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
                const gf = u.favorites || [];
                setFavorites(gf);
                localStorage.setItem('spaczo_favorites', JSON.stringify(gf));
              }
            })
            .catch(err => console.error('Google OAuth error:', err));
        }
      }
    }

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const stored = localStorage.getItem('spaczo_user');
        if (stored) setUser(JSON.parse(stored));
      } catch { localStorage.removeItem('spaczo_user'); }
    }

    // Global 401 interceptor — silently clears stale token so user sees
    // "Sign In" on next action instead of a broken alert popup. A 403 with
    // `banned` means an admin suspended this account: sign out and say why.
    let bannedNoticeShown = false;
    const interceptorId = axios.interceptors.response.use(
      res => res,
      err => {
        const status = err?.response?.status;
        const banned = status === 403 && err?.response?.data?.banned;
        if ((status === 401 || banned) && localStorage.getItem('spaczo_token')) {
          ['spaczo_token', 'spaczo_user', 'spaczo_favorites'].forEach(k => localStorage.removeItem(k));
          delete axios.defaults.headers.common['Authorization'];
          setUser(null); setToken(null);
          if (banned && !bannedNoticeShown) {
            bannedNoticeShown = true;
            if (isSupabaseReady()) supabase.auth.signOut().catch(() => {});
            setTimeout(() => window.alert(err.response.data.message || 'Your account has been suspended.'), 0);
          }
        }
        return Promise.reject(err);
      }
    );

    // Refresh the saved profile once per load, so role changes (e.g. being
    // made an admin) and suspensions take effect without signing in again.
    if (token) {
      axios.get('/api/auth/me')
        .then(res => {
          if (!res.data?.success || !res.data.user) return;
          setUser(prev => {
            const next = { ...(prev || {}), ...res.data.user };
            localStorage.setItem('spaczo_user', JSON.stringify(next));
            return next;
          });
        })
        .catch(() => {});
    }

    // Listen for Supabase auth state changes
    if (isSupabaseReady()) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (loggingOutRef.current) return;
        if (event === 'TOKEN_REFRESHED' && session) {
          if (!localStorage.getItem('spaczo_user')) return;
          const t = session.access_token;
          axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
          localStorage.setItem('spaczo_token', t);
          setToken(t);
        }
      });
      return () => { subscription.unsubscribe(); axios.interceptors.response.eject(interceptorId); };
    }
    return () => axios.interceptors.response.eject(interceptorId);
  }, []); // eslint-disable-line

  const _saveSession = (t, u) => {
    setToken(t); setUser(u);
    localStorage.setItem('spaczo_token', t);
    localStorage.setItem('spaczo_user', JSON.stringify(u));
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  };

  const _clearSession = () => {
    setUser(null); setToken(null); setFavorites([]);
    localStorage.removeItem('spaczo_token');
    localStorage.removeItem('spaczo_user');
    localStorage.removeItem('spaczo_favorites');
    delete axios.defaults.headers.common['Authorization'];
  };

  // ── Register ──────────────────────────────────────────────────
  // Always use the server which creates user via admin API (email auto-confirmed, no email OTP needed)
  const register = async (name, email, password, phone) => {
    // Remove any stale auth header so server doesn't pick up a stale token
    delete axios.defaults.headers.common['Authorization'];
    const res = await axios.post('/api/auth/register', { name, email, password, phone, ref: getJoinRef() || undefined });
    if (!res.data.success) throw new Error(res.data.message || 'Registration failed');
    _saveSession(res.data.token, res.data.user);
    const regFavs = res.data.user.favorites || [];
    setFavorites(regFavs);
    localStorage.setItem('spaczo_favorites', JSON.stringify(regFavs));
    return res.data.user;
  };

  // ── Login ────────────────────────────────────────────────────
  const login = async (email, password) => {
    if (isSupabaseReady()) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error && /banned/i.test(error.message || '')) {
        throw new Error('Your account has been suspended. Contact support@spaczo.com if you think this is a mistake.');
      }
      if (error) {
        // If email not confirmed, fix it via server and retry
        if (error.message?.toLowerCase().includes('confirm') || error.message?.toLowerCase().includes('not confirmed')) {
          try {
            const res = await axios.post('/api/auth/login', { email, password });
            if (res.data.success) {
              _saveSession(res.data.token, res.data.user);
              const f1 = res.data.user.favorites || [];
              setFavorites(f1);
              localStorage.setItem('spaczo_favorites', JSON.stringify(f1));
              return res.data.user;
            }
          } catch {}
        }
        throw new Error(error.message || 'Invalid email or password');
      }

      const t = data.session.access_token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;

      // Fetch profile + favorites from Express
      try {
        const res = await axios.get('/api/auth/me');
        const u   = res.data.user;
        _saveSession(t, u);
        const f2 = u.favorites || [];
        setFavorites(f2);
        localStorage.setItem('spaczo_favorites', JSON.stringify(f2));
        return u;
      } catch {
        const u = { id: data.user.id, email: data.user.email, name: data.user.user_metadata?.name || email, favorites: [] };
        _saveSession(t, u);
        setFavorites([]); localStorage.setItem('spaczo_favorites', '[]');
        return u;
      }
    }

    const res = await axios.post('/api/auth/login', { email, password });
    _saveSession(res.data.token, res.data.user);
    const f3 = res.data.user.favorites || [];
    setFavorites(f3);
    localStorage.setItem('spaczo_favorites', JSON.stringify(f3));
    return res.data.user;
  };

  // ── Change password (logged-in user) ─────────────────────────
  const changePassword = async (currentPassword, newPassword) => {
    if (!isSupabaseReady()) throw new Error('Not available');
    if (!user?.email) throw new Error('No email on account');
    const { error: authErr } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
    if (authErr) throw new Error('Current password is incorrect');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  };

  // ── Forgot password ──────────────────────────────────────────
  const forgotPassword = async (email) => {
    if (isSupabaseReady()) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin
      });
      if (error) throw new Error(error.message);
      return;
    }
    throw new Error('Password reset is not available');
  };

  // ── Reset password (after clicking email link) ────────────────
  const resetPassword = async (newPassword) => {
    if (isSupabaseReady()) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Reset link expired. Please request a new one.');
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw new Error(error.message);
      setRecoveryMode(false);
      return;
    }
    throw new Error('Password reset is not available');
  };

  // ── Avatar upload ────────────────────────────────────────────
  const updateAvatar = async (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    const res = await axios.post('/api/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (res.data.success) {
      const updated = { ...user, avatar: res.data.avatarUrl };
      setUser(updated);
      localStorage.setItem('spaczo_user', JSON.stringify(updated));
    }
    return res.data;
  };

  // ── Logout ───────────────────────────────────────────────────
  const logout = async () => {
    try { loggingOutRef.current = true; } catch {}
    try {
      ['spaczo_token','spaczo_user','spaczo_favorites'].forEach(k => localStorage.removeItem(k));
      Object.keys(localStorage).filter(k => k.startsWith('mylistings_')).forEach(k => localStorage.removeItem(k));
    } catch {}
    try { sessionStorage.clear(); } catch {}
    try { setUser(null); setToken(null); setFavorites([]); } catch {}
    try { delete axios.defaults.headers.common['Authorization']; } catch {}
    try { if (supabase) await supabase.auth.signOut(); } catch {}
    window.location.href = window.location.origin;
  };

  // ── Favorites ────────────────────────────────────────────────
  const toggleFavorite = async (propertyId) => {
    const newFavs = favorites.includes(propertyId)
      ? favorites.filter(id => id !== propertyId)
      : [...favorites, propertyId];
    setFavorites(newFavs);
    localStorage.setItem('spaczo_favorites', JSON.stringify(newFavs));
    if (token) {
      try { await axios.patch(`/api/auth/favorites/${propertyId}`); } catch {}
    }
  };

  const isFavorite = (id) => favorites.includes(id);

  return (
    <AuthContext.Provider value={{
      user, token, favorites, recoveryMode,
      login, register, logout,
      forgotPassword, resetPassword, changePassword,
      toggleFavorite, isFavorite, updateAvatar
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
